import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import StatCard from "../../components/common/StatCard";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../../components/Breadcrumb";
import { selectUser } from "../../services/reducers/user";
import CurrencyFormatter from "../../utils/CurrencyFormatter";
import DataTable from "react-data-table-component";
import Tooltip from "../../components/common/Tooltip";
import SaleDetailsDrawer from "../../components/sales/modals/SaleDetailsDrawer";
import { productsV2API } from "../../services/api/v2/products";
import { salesV2API } from "../../services/api/v2/sales";
import "./SalesManagementPage.css";

const SalesManagementPage = () => {
  const user = useSelector(selectUser);
  const location = useLocation();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSaleDrawer, setShowSaleDrawer] = useState(false);
  const [drawerSaleId, setDrawerSaleId] = useState(null);

  // Filtros - aceita estado inicial da navegação (ex: vindo do Dashboard)
  const [filters, setFilters] = useState({
    status: "all",
    paymentMethod: "all",
    dateRange: location.state?.dateRange || "all",
    product: "all",
    tracking: "all",
  });


  // Atualizar filtros quando navegar de volta com novo state
  useEffect(() => {
    if (location.state?.dateRange) {
      setFilters(prev => ({ ...prev, dateRange: location.state.dateRange }));
    }
  }, [location.state]);

  // Carregar vendas do produtor.
  // Como todos os filtros (status, payment_method, dateRange, tracking) sao
  // aplicados client-side em cima de `sales`, basta recarregar uma vez no
  // mount (ou quando o usuario logado muda).
  useEffect(() => {
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /**
   * Mapeia uma row crua de `sales` (V2) para o shape esperado pela UI legada.
   *
   * V2 retorna campos como `total_amount_cents`, `affiliate_user_id`, etc.
   * A UI V1 espera `total_amount` (em centavos), `affiliate_name`,
   * `affiliate_commission`, flags `is_producer`/`is_affiliate`/...
   *
   * Os campos derivados (`is_*`, `product_name`) sao calculados aqui a partir
   * dos dados ja disponiveis no client (lista de produtos do usuario + id do
   * usuario logado). Campos nao disponiveis no V2 raw (affiliate_name,
   * affiliate_commission, tracking_code, sale_status, payment_date) recebem
   * fallback neutro — a UI degrade pra "-" / 0.
   */
  const mapV2SaleRow = (saleRow, product, currentUserId) => {
    const ownerId = product?.user_id ?? null;
    const isProducer = ownerId !== null && Number(ownerId) === Number(currentUserId);
    const isAffiliate = saleRow.affiliate_user_id !== null
      && Number(saleRow.affiliate_user_id) === Number(currentUserId);
    const isManager = saleRow.manager_user_id !== null
      && Number(saleRow.manager_user_id) === Number(currentUserId);
    const isCoproducer = saleRow.coproducer_user_id !== null
      && Number(saleRow.coproducer_user_id) === Number(currentUserId);

    return {
      // ===== Campos que vem direto do V2 =====
      ...saleRow,
      // ===== Campos remapeados pra UI V1 =====
      // UI espera `total_amount` em centavos (CurrencyFormatter.centsToFormatted)
      total_amount: saleRow.total_amount_cents,
      product_name: product?.name || "Produto",
      product_image: product?.image_url || null,
      // ===== Campos derivados (papel do usuario na venda) =====
      is_producer: isProducer,
      is_affiliate: isAffiliate,
      is_manager: isManager,
      is_coproducer: isCoproducer,
      is_supplier: false,
      is_logistics_rep: false,
      // ===== Campos nao disponiveis no V2 raw =====
      affiliate_name: null,
      affiliate_commission: 0,
      tracking_code: null,
      sale_status: null,
      payment_date: null,
      producer_id: ownerId,
      affiliate_id: saleRow.affiliate_user_id,
      customer_id: saleRow.customer_user_id,
    };
  };

  const loadSales = async () => {
    try {
      setLoading(true);
      setError(null);

      // Passo 1: lista produtos do usuario (necessario porque V2 expoe
      // vendas por produto, nao um endpoint agregado).
      const productsRes = await productsV2API.listMine({ limit: 200 });
      const products = productsRes.data?.products || [];

      if (products.length === 0) {
        setSales([]);
        return;
      }

      // Passo 2: busca vendas em paralelo de todos os produtos do usuario.
      const productSalesArrays = await Promise.all(
        products.map((p) =>
          salesV2API
            .listByProduct(p.id, { limit: 200 })
            .then((res) => ({ product: p, sales: res.data?.sales || [] }))
            .catch(() => ({ product: p, sales: [] }))
        )
      );

      // Passo 3: achata + mapeia pro shape V1 + ordena por created_at DESC.
      const currentUserId = user?.id;
      const flat = productSalesArrays.flatMap(({ product, sales: rows }) =>
        rows.map((row) => mapV2SaleRow(row, product, currentUserId))
      );
      flat.sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });

      setSales(flat);
    } catch (err) {
      setError(err?.message || "Erro ao carregar vendas");
      console.error("Erro ao carregar vendas:", err);
    } finally {
      setLoading(false);
    }
  };

  // Retorna { startDate, endDate } como objetos Date pra filtragem client-side.
  // (V1 enviava start_date/end_date pro backend; V2 nao tem esse filtro,
  // entao filtramos no client.)
  const getDateRangeBoundaries = (range) => {
    const now = new Date();
    const startDate = new Date();

    switch (range) {
      case "today":
        startDate.setHours(0, 0, 0, 0);
        return { startDate, endDate: now };
      case "yesterday": {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return { startDate: yesterday, endDate: yesterdayEnd };
      }
      case "last7days":
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        return { startDate, endDate: now };
      case "last30days":
        startDate.setDate(now.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        return { startDate, endDate: now };
      case "last3months":
        startDate.setMonth(now.getMonth() - 3);
        startDate.setHours(0, 0, 0, 0);
        return { startDate, endDate: now };
      default:
        return null;
    }
  };

  /**
   * Callback após atualização da venda no modal
   */
  const handleSaleUpdated = () => {
    loadSales(); // Recarregar lista de vendas
  };


  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_payment: {
        color: "warning",
        icon: "solar:clock-circle-bold",
        text: "Aguardando Pagamento",
      },
      paid: { color: "info", icon: "solar:check-circle-bold", text: "Pago" },
      processing: {
        color: "primary",
        icon: "solar:settings-bold",
        text: "Processando",
      },
      shipped: {
        color: "success",
        icon: "solar:delivery-bold",
        text: "Enviado",
      },
      delivered: {
        color: "success",
        icon: "solar:home-2-bold",
        text: "Entregue",
      },
      cancelled: {
        color: "danger",
        icon: "solar:close-circle-bold",
        text: "Cancelado",
      },
      refunded: {
        color: "secondary",
        icon: "solar:restart-bold",
        text: "Reembolsado",
      },
    };

    const config = statusConfig[status] || statusConfig["pending_payment"];

    return (
      <span
        className={`badge bg-${config.color} text-white d-flex align-items-center gap-1`}
      >
        <Icon icon={config.icon} className="text-xs" />
        {config.text}
      </span>
    );
  };

  const getPaymentMethodIcon = (method) => {
    const methods = {
      credit_card: "solar:card-bold",
      debit_card: "solar:card-2-bold",
      pix: "solar:qr-code-bold",
      boleto: "solar:document-text-bold",
      after_pay: "solar:delivery-bold",
    };
    return methods[method] || "solar:card-bold";
  };

  const getPaymentMethodText = (method) => {
    const methods = {
      credit_card: "Cartão de Crédito",
      debit_card: "Cartão de Débito",
      pix: "PIX",
      boleto: "Boleto",
      after_pay: "Pagamento Após Entrega",
    };
    return methods[method] || method;
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      credit_card: "#6366f1",
      debit_card: "#8b5cf6",
      pix: "#10b981",
      boleto: "#f59e0b",
      after_pay: "#ef4444",
    };
    return colors[method] || "#6b7280";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (priceInCents) => {
    // ✅ CORRIGIDO: Agora usa CurrencyFormatter para converter centavos
    return CurrencyFormatter.centsToFormatted(priceInCents);
  };

  // Função para abreviar valores grandes
  const formatPriceCompact = (priceInCents) => {
    const valueInReais = priceInCents / 100;
    
    // Valores menores que 100 mil - formato completo
    if (valueInReais < 100000) {
      return CurrencyFormatter.centsToFormatted(priceInCents);
    }
    
    // Valores entre 100 mil e 1 milhão - formato com "mil"
    if (valueInReais < 1000000) {
      const thousands = (valueInReais / 1000).toFixed(1);
      return `R$ ${thousands} mil`;
    }
    
    // Valores acima de 1 milhão - formato com "mi"
    const millions = (valueInReais / 1000000).toFixed(2);
    return `R$ ${millions} mi`;
  };

  // Funcao para gerar TAGs de relacionamento do usuario com a venda
  const getRoleTags = (sale) => {
    const tags = [];

    if (sale.is_producer) {
      tags.push({ label: 'Meu produto', color: '#10b981', icon: 'mdi:store' });
    }
    if (sale.is_coproducer) {
      tags.push({ label: 'Coprodutor', color: '#8b5cf6', icon: 'mdi:handshake' });
    }
    if (sale.is_affiliate) {
      tags.push({ label: 'Sou afiliado', color: '#3b82f6', icon: 'mdi:link' });
    }
    if (sale.is_manager) {
      tags.push({ label: 'Gerente', color: '#f59e0b', icon: 'mdi:account-tie' });
    }
    if (sale.is_supplier) {
      tags.push({ label: 'Fornecedor', color: '#ec4899', icon: 'mdi:truck-delivery' });
    }
    if (sale.is_logistics_rep) {
      tags.push({ label: 'Logistica', color: '#06b6d4', icon: 'mdi:map-marker' });
    }

    return tags;
  };

  // Mapeamento de status para nomes da legenda
  const getStatusLegendName = (status) => {
    const legendNames = {
      paid: "Pagamento Aprovado",
      pending: "Pagamento Pendente",
      processing: "Processando Pagamento",
      scheduled: "Pagamento Agendado",
      cancelled: "Pagamento Cancelado",
      refunded: "Pagamento Reembolsado",
    };
    return (
      legendNames[status] || status.charAt(0).toUpperCase() + status.slice(1)
    );
  };

  const getSimpleStatusBadge = (sale) => {
    const ps = (sale.payment_status || "").toLowerCase();

    const statusConfig = {
      paid: {
        color: "#10b981",
        icon: "solar:check-circle-bold",
      },
      pending: {
        color: "#f59e0b",
        icon: "solar:clock-circle-bold",
      },
      processing: {
        color: "#3b82f6",
        icon: "solar:settings-bold",
      },
      scheduled: {
        color: "#3b82f6",
        icon: "solar:calendar-bold",
      },
      cancelled: {
        color: "#ef4444",
        icon: "solar:close-circle-bold",
      },
      refunded: {
        color: "#ef4444",
        icon: "solar:restart-bold",
      },
    };

    const config = statusConfig[ps] || statusConfig["pending"];
    const legendName = getStatusLegendName(ps);

    return (
      <Tooltip content={legendName}>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: `${config.color}20`,
            border: `2px solid ${config.color}`,
            cursor: "pointer",
          }}
        >
          <Icon
            icon={config.icon}
            style={{
              fontSize: "16px",
              color: config.color,
            }}
          />
        </div>
      </Tooltip>
    );
  };

  // ===== Busca global, seleção e exportação CSV =====
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSmall, setIsSmall] = useState(false);
  const [visibleCount, setVisibleCount] = useState(15);

  // Responsividade básica: considerar mobile quando < 768px
  useEffect(() => {
    const onResize = () => setIsSmall(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const filteredSales = useMemo(() => {
    let result = sales;

    // Filtro por status (client-side)
    if (filters.status !== 'all') {
      result = result.filter((s) => {
        const ps = (s.payment_status || '').toLowerCase();
        const ss = (s.sale_status || '').toLowerCase();
        return ps === filters.status || ss === filters.status;
      });
    }

    // Filtro por método de pagamento (client-side)
    if (filters.paymentMethod !== 'all') {
      result = result.filter((s) => s.payment_method === filters.paymentMethod);
    }

    // Filtro por periodo (client-side, baseado em created_at).
    // V2 API nao expoe start_date/end_date, entao a filtragem acontece aqui.
    if (filters.dateRange !== 'all') {
      const boundaries = getDateRangeBoundaries(filters.dateRange);
      if (boundaries) {
        const startTs = boundaries.startDate.getTime();
        const endTs = boundaries.endDate.getTime();
        result = result.filter((s) => {
          if (!s.created_at) return false;
          const t = new Date(s.created_at).getTime();
          return t >= startTs && t <= endTs;
        });
      }
    }

    // Filtro por rastreio
    if (filters.tracking !== 'all') {
      result = result.filter((s) => {
        const hasTracking = !!s.tracking_code;
        const ss = (s.sale_status || '').toLowerCase();
        switch (filters.tracking) {
          case 'with_tracking': return hasTracking;
          case 'without_tracking': return !hasTracking && ss !== 'cancelled' && ss !== 'refunded';
          case 'shipped': return ss === 'shipped';
          case 'delivered': return ss === 'delivered';
          default: return true;
        }
      });
    }

    // Filtro por busca textual
    const text = searchText.trim().toLowerCase();
    if (text) {
      result = result.filter((s) => {
        return (
          String(s.id).includes(text) ||
          (s.customer_name || "").toLowerCase().includes(text) ||
          (s.customer_email || "").toLowerCase().includes(text) ||
          (s.affiliate_name || "").toLowerCase().includes(text) ||
          (s.product_name || "").toLowerCase().includes(text) ||
          (s.payment_method || "").toLowerCase().includes(text) ||
          (s.sale_status || "").toLowerCase().includes(text) ||
          (s.payment_status || "").toLowerCase().includes(text)
        );
      });
    }

    return result;
  }, [sales, searchText, filters]);

  // Resetar contagem visível quando filtros/busca mudam
  useEffect(() => {
    setVisibleCount(15);
  }, [searchText, filters]);

  const visibleSales = useMemo(() => {
    return filteredSales.slice(0, visibleCount);
  }, [filteredSales, visibleCount]);

  const hasMore = visibleCount < filteredSales.length;

  const buildCsv = (rows) => {
    const headers = [
      "Venda",
      "Cliente",
      "Email",
      "Forma",
      "DataPedido",
      "DataPagamento",
      "Afiliado",
      "SaleStatus",
      "PaymentStatus",
      "Comissao",
      "Valor",
    ];
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((r) =>
      [
        `#${r.id}`,
        r.customer_name || "",
        r.customer_email || "",
        r.payment_method || "",
        r.created_at || "",
        r.payment_date || "",
        r.affiliate_name || "",
        r.sale_status || "",
        r.payment_status || "",
        CurrencyFormatter.centsToFormatted(
          parseFloat(r.affiliate_commission || 0)
        ),
        CurrencyFormatter.centsToFormatted(parseFloat(r.total_amount || 0)),
      ]
        .map(escape)
        .join(",")
    );
    return [headers.join(","), ...lines].join("\n");
  };

  const exportCsv = () => {
    const rows = selectedRows.length > 0 ? selectedRows : filteredSales;
    const blob = new Blob([buildCsv(rows)], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vendas_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== Colunas da tabela (DataTable) - Layout consolidado =====
  const baseColumns = [
    {
      name: "Venda",
      selector: (row) => row.id,
      sortable: true,
      minWidth: "155px",
      grow: 1,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          {getStatusDot(row)}
          <div style={{ lineHeight: 1.3 }}>
            <span className="fw-semibold" style={{ fontSize: '13px' }}>#{row.id}</span>
            {row.product_name && (
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {row.product_name}
              </div>
            )}
            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '1px' }}>
              {formatDate(row.created_at)}
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Comprador",
      selector: (row) => row.customer_name,
      sortable: true,
      minWidth: "180px",
      grow: 1.8,
      cell: (row) => {
        const tags = getRoleTags(row);
        return (
          <div style={{ lineHeight: 1.3 }}>
            <span className="fw-medium" style={{ fontSize: '13px' }}>
              {row.customer_name}
            </span>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>
              {row.customer_email}
            </div>
            {tags.length > 0 && (
              <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', marginTop: '4px' }}>
                {tags.map((tag, idx) => (
                  <Tooltip key={idx} content={tag.label}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '2px',
                        padding: '1px 5px',
                        borderRadius: '3px',
                        fontSize: '9px',
                        fontWeight: 600,
                        color: '#fff',
                        backgroundColor: tag.color,
                        cursor: 'pointer',
                      }}
                    >
                      <Icon icon={tag.icon} style={{ fontSize: '10px' }} />
                      {tag.label}
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: "Pagamento",
      sortable: true,
      minWidth: "165px",
      grow: 1,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: "30px",
              height: "30px",
              borderRadius: "8px",
              backgroundColor: getPaymentMethodColor(row.payment_method),
              color: "white",
            }}
          >
            <Icon
              icon={getPaymentMethodIcon(row.payment_method)}
              style={{ fontSize: "14px" }}
            />
          </div>
          <div style={{ lineHeight: 1.3 }}>
            <span style={{ fontSize: '12px', fontWeight: 500 }}>
              {getPaymentMethodText(row.payment_method)}
            </span>
            <div style={{ marginTop: '3px' }}>
              {getPaymentStatusBadge(row.payment_status)}
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Afiliado",
      selector: (row) => row.affiliate_name || "",
      sortable: true,
      minWidth: "130px",
      grow: 1,
      cell: (row) => {
        const commission = parseFloat(row.affiliate_commission || 0);
        return (
          <div style={{ lineHeight: 1.3 }}>
            <span className="fw-medium" style={{ fontSize: '12px' }}>
              {row.affiliate_name || "-"}
            </span>
            {commission > 0 && (
              <div style={{ fontSize: '11px', color: '#6366f1', fontWeight: 600, marginTop: '2px' }}>
                {formatPrice(commission)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: "Valor",
      selector: (row) => parseFloat(row.total_amount || 0),
      sortable: true,
      right: true,
      minWidth: "110px",
      grow: 0.6,
      cell: (row) => {
        const amount = parseFloat(row.total_amount || 0);
        return (
          <div className="text-end">
            <span
              className="fw-bold"
              style={{ fontSize: '14px', color: '#10b981' }}
            >
              {formatPrice(amount)}
            </span>
          </div>
        );
      },
    },
    {
      name: "",
      button: true,
      allowOverflow: true,
      center: true,
      width: "50px",
      cell: (row) => (
        <button
          title="Ver detalhes"
          onClick={() => { setDrawerSaleId(row.id); setShowSaleDrawer(true); }}
          className="btn btn-outline-primary-600 text-primary-600 btn-sm d-flex align-items-center justify-content-center"
          style={{ width: 30, height: 28, padding: 0, borderRadius: '6px' }}
        >
          <Icon icon="solar:eye-bold" style={{ fontSize: '14px' }} />
        </button>
      ),
    },
  ];

  // Em telas pequenas, ocultar colunas menos essenciais e mostrar em detalhes expansíveis
  const visibleColumns = useMemo(() => {
    if (!isSmall) return baseColumns;
    return baseColumns.filter((col) => {
      const name = String(col.name);
      return !["Afiliado", "Pagamento"].includes(name);
    });
  }, [baseColumns, isSmall]);

  const ExpandableRow = ({ data }) => (
    <div className="p-3" style={{ backgroundColor: '#f8fafc' }}>
      <div className="row g-2">
        <div className="col-6">
          <small className="text-muted" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pagamento</small>
          <div className="d-flex align-items-center gap-2 mt-1">
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "6px",
                backgroundColor: getPaymentMethodColor(data.payment_method),
                color: "white",
              }}
            >
              <Icon icon={getPaymentMethodIcon(data.payment_method)} style={{ fontSize: "12px" }} />
            </div>
            <span style={{ fontSize: '12px' }}>{getPaymentMethodText(data.payment_method)}</span>
          </div>
          <div style={{ marginTop: '4px' }}>
            {getPaymentStatusBadge(data.payment_status)}
          </div>
        </div>
        <div className="col-6">
          <small className="text-muted" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Afiliado</small>
          <div style={{ fontSize: 12, marginTop: '4px', fontWeight: 500 }}>{data.affiliate_name || "-"}</div>
          {parseFloat(data.affiliate_commission || 0) > 0 && (
            <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, marginTop: '2px' }}>
              {formatPrice(parseFloat(data.affiliate_commission))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ===== Helpers de métricas (cards do topo) =====
  const getTotals = () => {
    const paidSales = sales.filter((s) => s.payment_status === "paid");
    const pendingPaymentSales = sales.filter((s) =>
      ["pending", "processing"].includes(s.payment_status)
    );
    const consideredForTotal = sales.filter((s) =>
      ["paid", "pending", "processing"].includes(s.payment_status)
    );

    const receitaTotalPaga = paidSales.reduce(
      (sum, s) => sum + parseFloat(s.total_amount || 0),
      0
    );
    const totalPendentePagamento = pendingPaymentSales.reduce(
      (sum, s) => sum + parseFloat(s.total_amount || 0),
      0
    );
    const montanteTotalVendas = consideredForTotal.reduce(
      (sum, s) => sum + parseFloat(s.total_amount || 0),
      0
    );
    const totalComissoesPagas = paidSales.reduce(
      (sum, s) => sum + parseFloat(s.affiliate_commission || 0),
      0
    );

    return {
      receitaTotalPaga,
      totalPendentePagamento,
      montanteTotalVendas,
      totalComissoesPagas,
    };
  };

  // ===== Legenda de status com ícones coloridos =====
  const StatusLegend = () => (
    <div className="d-flex flex-wrap align-items-center justify-content-end gap-4 mb-3">
      <div className="d-flex align-items-center gap-2">
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            backgroundColor: "#3b82f620",
            border: "2px solid #3b82f6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            icon="solar:settings-bold"
            style={{ fontSize: "12px", color: "#3b82f6" }}
          />
        </div>
        <span className="text-secondary" style={{ fontSize: "12px" }}>
          Em Processamento | Agendado
        </span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            backgroundColor: "#10b98120",
            border: "2px solid #10b981",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            icon="solar:check-circle-bold"
            style={{ fontSize: "12px", color: "#10b981" }}
          />
        </div>
        <span className="text-secondary" style={{ fontSize: "12px" }}>
          Pagamento Aprovado
        </span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            backgroundColor: "#f59e0b20",
            border: "2px solid #f59e0b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            icon="solar:clock-circle-bold"
            style={{ fontSize: "12px", color: "#f59e0b" }}
          />
        </div>
        <span className="text-secondary" style={{ fontSize: "12px" }}>
          Aguardando Pagamento | Em Análise | Estorno Pendente
        </span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div
          style={{
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            backgroundColor: "#ef444420",
            border: "2px solid #ef4444",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            icon="solar:close-circle-bold"
            style={{ fontSize: "12px", color: "#ef4444" }}
          />
        </div>
        <span className="text-secondary" style={{ fontSize: "12px" }}>
          Cancelada | Chargeback | Devolvida | Frustrada
        </span>
      </div>
    </div>
  );

  // Mapeia status para cor do ponto (tabela)
  const getStatusDot = (sale) => {
    const ps = (sale.payment_status || "").toLowerCase();

    // Legendas:
    // Azul (#3B82F6): Em Processamento | Agendado
    // Verde (#10B981): Pagamento Aprovado
    // Amarelo (#F59E0B): Aguardando Pagamento | Em Análise | Estorno Pendente
    // Vermelho (#EF4444): Cancelada | Chargeback | Devolvida | Frustrada

    let color = "#9CA3AF";
    let hint = "Desconhecido";

    if (ps === "paid") {
      color = "#10B981";
      hint = "Pagamento Aprovado";
    } else if (ps === "scheduled" || ps === "processing") {
      color = "#3B82F6";
      hint = "Em Processamento | Agendado";
    } else if (ps === "pending") {
      color = "#F59E0B";
      hint = "Aguardando Pagamento | Em Análise | Estorno Pendente";
    } else if (
      ["cancelled", "refused", "chargeback", "refunded"].includes(ps)
    ) {
      color = "#EF4444";
      hint = "Cancelada | Chargeback | Devolvida | Frustrada";
    }

    return (
      <span
        title={hint}
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: color,
        }}
      />
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const ps = (paymentStatus || "").toLowerCase();
    const map = {
      paid: { text: "Pago", className: "badge bg-success" },
      pending: { text: "Pendente", className: "badge bg-warning text-dark" },
      processing: { text: "Processando", className: "badge bg-primary" },
      scheduled: { text: "Agendado", className: "badge bg-primary" },
      cancelled: { text: "Cancelado", className: "badge bg-danger" },
      refunded: { text: "Estornado", className: "badge bg-danger" },
      chargeback: { text: "Chargeback", className: "badge bg-danger" },
      refused: { text: "Recusado", className: "badge bg-danger" },
    };
    const cfg = map[ps] || {
      text: ps || "Desconhecido",
      className: "badge bg-secondary",
    };
    return <span className={cfg.className}>{cfg.text}</span>;
  };

  // Produtos únicos para filtro
  const uniqueProducts = [...new Set(sales.map((sale) => sale.product_name))];

  return (
    <MasterLayout>
      <div className="dashboard-main-body">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
          <span className="fw-semibold mb-0" style={{ fontSize: '22px', display: 'inline-flex', alignItems: 'center' }}>Relatório de Vendas</span>
          <Breadcrumb
            title="Relatório de Vendas"
            items={[
              { label: "Home", link: "/Dashboard" },
              { label: "Vendas", link: "/sales-management" },
            ]}
          />
        </div>

        {/* Métricas Resumo */}
        <div
          className="row g-3 sales-top-cards"
          style={{ marginBottom: "24px" }}
        >
          {(() => {
            const t = getTotals();
            return (
              <div className="col-md-3">
                <StatCard
                  title="Receita total"
                  value={formatPrice(t.receitaTotalPaga)}
                  subtitle="Vendas pagas"
                  icon="solar:bag-bold"
                  variant="blue"
                  onClickValue={() => navigate('/withdrawals')}
                  onClickIcon={() => navigate('/withdrawals')}
                />
              </div>
            );
          })()}
          <div className="col-md-3">
            <StatCard
              title="Total pendente"
              value={formatPrice(getTotals().totalPendentePagamento)}
              subtitle="Aguardando pagamento"
              icon="solar:dollar-bold"
              variant="purple"
              onClickValue={() => setFilters(prev => ({ ...prev, status: 'pending_payment' }))}
              onClickIcon={() => setFilters(prev => ({ ...prev, status: 'pending_payment' }))}
            />
          </div>

          <div className="col-md-3">
            <StatCard
              title="Montante total de vendas"
              value={formatPrice(getTotals().montanteTotalVendas)}
              subtitle="Pagas + Pendentes"
              icon="solar:delivery-bold"
              variant="green"
              onClickValue={() => navigate('/dashboard')}
              onClickIcon={() => navigate('/dashboard')}
            />
          </div>

          <div className="col-md-3">
            <StatCard
              title="Total de comissões"
              value={formatPrice(getTotals().totalComissoesPagas)}
              subtitle="Pedidos pagos"
              icon="solar:user-heart-bold"
              variant="orange"
              onClickValue={() => navigate('/commissions/dashboard')}
              onClickIcon={() => navigate('/commissions/dashboard')}
            />
          </div>
        </div>

        {/* Legenda de Status */}
        <StatusLegend />

        {/* Lista de Vendas */}
        <div className="card radius-12 ">
          <div className="card-header border-bottom bg-base py-16 px-24">
            <div className="d-flex justify-content-between align-items-center flex-column flex-md-row align-items-start align-items-md-center gap-3">
              <span className="fw-semibold mb-0" style={{ fontSize: '16px' }}>
                Vendas dos Meus Produtos
              </span>

              {/* Filtros e Busca no Header */}
              <div className="d-flex align-items-center gap-2 flex-wrap flex-lg-nowrap w-100 w-md-auto justify-content-end">
                {/* Busca */}
                <input
                  type="text"
                  className="form-control flex-grow-1 flex-lg-grow-0"
                  placeholder="Buscar por código, cliente..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{
                    minWidth: "200px",
                    // maxWidth: "240px",
                    height: "38px",
                    fontSize: "14px",
                  }}
                />

                {/* Filtro Status */}
                <select
                  className="form-select flex-grow-1 flex-lg-grow-0"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                  style={{
                    // backgroundColor: 'red',
                    // padding: '300px',
                    minWidth: "140px",
                    maxWidth: "150px",
                    height: "38px",
                    fontSize: "14px",
                  }}
                >
                  <option value="all">Todos Status</option>
                  <option value="pending_payment">Pendente</option>
                  <option value="paid">Pago</option>
                  <option value="processing">Processando</option>
                  <option value="shipped">Enviado</option>
                  <option value="delivered">Entregue</option>
                  <option value="scheduled">Agendado</option>
                  <option value="cancelled">Cancelado</option>
                  <option value="refunded">Reembolsado</option>
                </select>

                {/* Filtro Pagamento */}
                <select
                  className="form-select flex-grow-1 flex-lg-grow-0"
                  value={filters.paymentMethod}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      paymentMethod: e.target.value,
                    }))
                  }
                  style={{
                    minWidth: "190px",
                    // maxWidth: '160px',

                    height: "38px",
                    fontSize: "14px",
                  }}
                >
                  <option value="all">Formas de Pagamento</option>
                  <option value="credit_card">Cartão de Crédito</option>
                  <option value="debit_card">Cartão de Débito</option>
                  <option value="pix">PIX</option>
                  <option value="boleto">Boleto</option>
                </select>

                {/* Filtro Período */}
                <select
                  className="form-select flex-grow-1 flex-lg-grow-0"
                  value={filters.dateRange}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      dateRange: e.target.value,
                    }))
                  }
                  style={{
                    minWidth: "160px",
                    // maxWidth: '140px',
                    height: "38px",
                    fontSize: "14px",
                  }}
                >
                  <option value="all">Todo período</option>
                  <option value="today">Hoje</option>
                  <option value="yesterday">Ontem</option>
                  <option value="last7days">Últimos 7 dias</option>
                  <option value="last30days">Últimos 30 dias</option>
                  <option value="last3months">Últimos 3 meses</option>
                </select>

                {/* Filtro Rastreio */}
                <select
                  className="form-select flex-grow-1 flex-lg-grow-0"
                  value={filters.tracking}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      tracking: e.target.value,
                    }))
                  }
                  style={{
                    minWidth: "140px",
                    maxWidth: "160px",
                    height: "38px",
                    fontSize: "14px",
                  }}
                >
                  <option value="all">Rastreio</option>
                  <option value="with_tracking">Com rastreio</option>
                  <option value="without_tracking">Sem rastreio</option>
                  <option value="shipped">Enviado</option>
                  <option value="delivered">Entregue</option>
                </select>

                {/* Exportar CSV */}
                <button
                  className="btn btn-outline-primary d-flex align-items-center justify-content-center flex-grow-1 flex-lg-grow-0"
                  onClick={exportCsv}
                  style={{
                    minWidth: "140px",
                    height: "38px",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Icon
                    icon="solar:download-bold"
                    className="me-2"
                    style={{ fontSize: "16px" }}
                  />
                  <span className="d-none d-sm-inline">Exportar CSV</span>
                  <span className="d-inline d-sm-none">CSV</span>
                </button>
              </div>
            </div>
          </div>

          <div className="card-body p-0" style={{ backgroundColor: "none" }}>
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" />
                <p className="text-muted">Carregando vendas...</p>
              </div>
            ) : error ? (
              <div className="text-center py-5">
                <Icon
                  icon="solar:close-circle-bold"
                  className="text-danger mb-3"
                  style={{ fontSize: "48px" }}
                />
                <span className="text-danger mb-2 d-block fw-semibold" style={{ fontSize: '16px' }}>Erro ao carregar vendas</span>
                <p className="text-muted mb-3">{error}</p>
                <button
                  onClick={loadSales}
                  className="btn btn-primary-600 text-white"
                >
                  Tentar Novamente
                </button>
              </div>
            ) : sales.length === 0 ? (
              <div className="text-center py-5">
                <Icon
                  icon="solar:chart-square-bold"
                  className="text-secondary-light mb-3"
                  style={{ fontSize: "64px" }}
                />
                <span className="text-secondary-light mb-2 d-block fw-semibold" style={{ fontSize: '16px' }}>
                  Nenhuma venda encontrada
                </span>
                <p className="text-muted mb-3">
                  Você ainda não possui vendas ou não há vendas nos filtros
                  selecionados.
                </p>
              </div>
            ) : (
              <>
                <DataTable
                  columns={visibleColumns}
                  data={visibleSales}
                  noDataComponent={
                    <div className="text-center py-4 text-muted" style={{ fontSize: '14px' }}>
                      Nenhuma venda encontrada para os filtros selecionados.
                    </div>
                  }
                  highlightOnHover
                  defaultSortFieldId={1}
                  responsive
                  persistTableHead
                  selectableRows
                  onSelectedRowsChange={({ selectedRows }) =>
                    setSelectedRows(selectedRows)
                  }
                  clearSelectedRows={false}
                  dense={isSmall}
                  expandableRows={isSmall}
                  expandableRowsComponent={ExpandableRow}
                  customStyles={{
                    headRow: {
                      style: {
                        backgroundColor: "#f8fafc",
                        borderBottom: "2px solid #e2e8f0",
                        minHeight: "44px",
                      },
                    },
                    headCells: {
                      style: {
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        paddingTop: "12px",
                        paddingBottom: "12px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                      },
                    },
                    rows: {
                      style: {
                        minHeight: "76px",
                        fontSize: "13px",
                        borderBottom: "1px solid #f1f5f9",
                      },
                      highlightOnHoverStyle: {
                        backgroundColor: "#f8fafc",
                        borderBottomColor: "#e2e8f0",
                        transition: "background-color 0.15s ease",
                      },
                    },
                    cells: {
                      style: {
                        fontSize: "13px",
                        whiteSpace: "normal",
                        paddingTop: "12px",
                        paddingBottom: "12px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                      },
                    },
                  }}
                />

                {/* Botão Carregar Mais */}
                {hasMore && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '16px',
                      borderTop: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                      gap: '12px',
                    }}
                  >
                    <button
                      onClick={() => setVisibleCount(prev => prev + 15)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 24px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        color: '#374151',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                        e.currentTarget.style.borderColor = '#6366f1';
                        e.currentTarget.style.color = '#6366f1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.color = '#374151';
                      }}
                    >
                      <Icon icon="mdi:chevron-down" style={{ fontSize: '18px' }} />
                      Carregar mais
                    </button>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                      {visibleCount} de {filteredSales.length} vendas
                    </span>
                  </div>
                )}

                {/* Indicador quando todas foram carregadas */}
                {!hasMore && filteredSales.length > 15 && (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '12px',
                      fontSize: '12px',
                      color: '#9ca3af',
                      borderTop: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                      borderBottomLeftRadius: '12px',
                      borderBottomRightRadius: '12px',
                    }}
                  >
                    Todas as {filteredSales.length} vendas carregadas
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Drawer de Detalhes da Venda */}
      <SaleDetailsDrawer
        show={showSaleDrawer}
        onHide={() => { setShowSaleDrawer(false); setDrawerSaleId(null); }}
        saleId={drawerSaleId}
        onSaleUpdated={handleSaleUpdated}
      />
    </MasterLayout>
  );
};

export default SalesManagementPage;
